// Simple localStorage-based auth helpers for admin + student

const ADMIN_TOKEN = 'mb_admin_token';
const ADMIN_INFO = 'mb_admin_info';
const STUDENT_TOKEN = 'mb_student_token';
const STUDENT_INFO = 'mb_student_info';

export function saveAdminSession(token, admin) {
    localStorage.setItem(ADMIN_TOKEN, token);
    localStorage.setItem(ADMIN_INFO, JSON.stringify(admin));
}

export function getAdminSession() {
    const token = localStorage.getItem(ADMIN_TOKEN);
    const raw = localStorage.getItem(ADMIN_INFO);
    if (!token || !raw) return null;
    try {
        return { token, admin: JSON.parse(raw) };
    } catch {
        return null;
    }
}

export function clearAdminSession() {
    localStorage.removeItem(ADMIN_TOKEN);
    localStorage.removeItem(ADMIN_INFO);
}

export function saveStudentSession(token, student) {
    localStorage.setItem(STUDENT_TOKEN, token);
    localStorage.setItem(STUDENT_INFO, JSON.stringify(student));
}

export function getStudentSession() {
    const token = localStorage.getItem(STUDENT_TOKEN);
    const raw = localStorage.getItem(STUDENT_INFO);
    if (!token || !raw) return null;
    try {
        return { token, student: JSON.parse(raw) };
    } catch {
        return null;
    }
}

export function clearStudentSession() {
    localStorage.removeItem(STUDENT_TOKEN);
    localStorage.removeItem(STUDENT_INFO);
}

export function isSuperAdmin() {
    const s = getAdminSession();
    return s?.admin?.role === 'super_admin';
}

export function isViewer() {
    const s = getAdminSession();
    return s?.admin?.role === 'viewer';
}

// ---------- Granular per-section admin permissions ----------
// Must stay in sync with backend auth.ADMIN_SECTIONS
export const ADMIN_SECTIONS = [
    { key: 'overview', label: 'Overview', path: '/admin' },
    { key: 'bookings', label: 'Bookings', path: '/admin/bookings' },
    { key: 'mentors', label: 'Mentors', path: '/admin/mentors' },
    { key: 'students', label: 'Students', path: '/admin/students' },
    { key: 'feedback_form', label: 'Feedback form', path: '/admin/feedback-form' },
    { key: 'feedback_submissions', label: 'Feedback', path: '/admin/feedback-submissions' },
    { key: 'settings', label: 'Settings', path: '/admin/settings' },
];

const LEVEL_RANK = { none: 0, view: 1, edit: 2 };

// Legacy viewer accounts (no stored permissions map) default to view-only everywhere.
export function getPermission(section) {
    const s = getAdminSession();
    if (!s?.admin) return 'none';
    if (s.admin.role === 'super_admin') return 'edit';
    const perms = s.admin.permissions;
    if (!perms || typeof perms !== 'object') return 'view';
    return LEVEL_RANK[perms[section]] !== undefined ? perms[section] : 'none';
}

export function canView(section) {
    return LEVEL_RANK[getPermission(section)] >= LEVEL_RANK.view;
}

export function canEdit(section) {
    return LEVEL_RANK[getPermission(section)] >= LEVEL_RANK.edit;
}

// ---------- Granular sub-access (show/hide) within a section ----------
// Must stay in sync with backend auth.ADMIN_SUB_SECTIONS
export const SUB_SECTIONS = {
    overview: [
        { key: 'stat_upcoming', label: 'Upcoming stat' },
        { key: 'stat_completed', label: 'Completed stat' },
        { key: 'stat_mentor_cancelled', label: 'Mentor cancelled stat' },
        { key: 'stat_candidate_cancelled', label: 'Candidate cancelled stat' },
        { key: 'stat_mentor_rescheduled', label: 'Mentor rescheduled stat' },
        { key: 'stat_candidate_rescheduled', label: 'Candidate rescheduled stat' },
        { key: 'stat_students', label: 'Students stat' },
        { key: 'stat_credits', label: 'Credits outstanding stat' },
        { key: 'stat_mentors', label: 'Mentors connected stat' },
        { key: 'stat_feedback', label: 'Feedback submissions stat' },
        { key: 'upcoming_list', label: 'Next 5 upcoming sessions list' },
    ],
    bookings: [
        { key: 'upcoming', label: 'Upcoming' },
        { key: 'completed', label: 'Completed' },
        { key: 'mentor_cancelled', label: 'Mentor cancelled' },
        { key: 'candidate_cancelled', label: 'Candidate cancelled' },
        { key: 'mentor_rescheduled', label: 'Mentor rescheduled' },
        { key: 'candidate_rescheduled', label: 'Candidate rescheduled' },
    ],
    feedback_submissions: [
        { key: 'mentor_feedback', label: 'Mentor feedback' },
        { key: 'student_feedback', label: 'Candidate feedback' },
    ],
};

// Whether the current admin may see a specific sub-item within a section.
// Legacy accounts (no stored sub_permissions) default to all visible.
export function canViewSub(section, subKey) {
    const s = getAdminSession();
    if (!s?.admin) return false;
    if (s.admin.role === 'super_admin') return true;
    if (!canView(section)) return false;
    const sp = s.admin.sub_permissions;
    if (!sp || typeof sp !== 'object' || !sp[section]) return true;
    return sp[section][subKey] !== false;
}

// True only when a sub-item is EXPLICITLY hidden (toggled off) for this admin.
// Used to hide sub-parts without regressing users whose section isn't configured.
export function isSubHidden(section, subKey) {
    const s = getAdminSession();
    if (!s?.admin || s.admin.role === 'super_admin') return false;
    const sp = s.admin.sub_permissions;
    if (!sp || typeof sp !== 'object' || !sp[section]) return false;
    return sp[section][subKey] === false;
}

// Default sub_permissions object (everything visible) for building forms.
export function defaultSubPermissions() {
    const out = {};
    for (const section of Object.keys(SUB_SECTIONS)) {
        out[section] = {};
        for (const item of SUB_SECTIONS[section]) out[section][item.key] = true;
    }
    return out;
}

// First admin route the current user is allowed to see (used for redirects).
export function firstAccessiblePath() {
    const target = ADMIN_SECTIONS.find((s) => canView(s.key));
    if (target) return target.path;
    if (isSuperAdmin()) return '/admin/users';
    return null; // no access at all
}

// Refresh the cached admin object (e.g. after /admin/me returns updated permissions).
export function updateStoredAdmin(admin) {
    const token = localStorage.getItem(ADMIN_TOKEN);
    if (!token || !admin) return;
    localStorage.setItem(ADMIN_INFO, JSON.stringify(admin));
}
